import { Box, Button, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Controls,
  ReactFlow,
  ViewportPortal,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useEffect, useMemo, useRef, useState } from "react";

import ChainCardNode from "./ChainCardNode";
import ChainEdge from "./ChainEdge";
import {
  AREA_HEAD,
  Arrangement,
  AreaBox,
  ChainNode,
  Position,
  Size,
  columnHeads,
  placeAreas,
  placeCards,
  runLayout,
  tierLayout,
  toFlowEdges,
  toLayoutInput,
} from "./chainLayout";
import { Highlight, SKIP_EDGES_DRAWN, VisibleChain } from "./chainModel";
import { TOOL_BUTTON_SX } from "./ValueChainControls";
import { chainPng, chainPngFilename, downloadPng } from "./exportPng";

const NODE_TYPES = { chainCard: ChainCardNode };
const EDGE_TYPES = { chainEdge: ChainEdge };

const FIT_VIEW_OPTIONS = { padding: 0.15, maxZoom: 1 };

export interface ChainCanvasProps {
  chain: VisibleChain;
  // The cards and edges a maker highlight keeps lit; null for none
  highlight?: Highlight | null;
  // How a tier view arranges its areas; null for the layered layout of the
  // read's columns
  arrangement?: Arrangement | null;
}

// Where the cards go and the boxes of the areas: the layered layout's, or a
// tier view's
const arrange = (
  chain: VisibleChain,
  sizes: ReadonlyMap<string, Size>,
  arrangement: Arrangement | null,
): { placed: Map<string, Position>; areas: AreaBox[] } => {
  if (!arrangement) {
    return { placed: runLayout(toLayoutInput(chain, sizes)), areas: [] };
  }
  const { positions, areas } = tierLayout(chain, sizes, arrangement);
  return { placed: positions, areas };
};

// The canvas of the chain: its cards at the positions of the layered layout
// (or of a tier view, in the boxes of their areas), the stated edges between
// them and the head of each column on one line above them. The layout runs
// once for each chain it is given, and once more when the cards have been
// measured; a change of highlight only dims and lights, in place. It is
// drawn inside the page's canvas provider, so that the toolbar can fit and
// export it.
const ChainCanvas: React.FC<ChainCanvasProps> = ({
  chain,
  highlight = null,
  arrangement = null,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<ChainNode>([]);
  const [positions, setPositions] = useState<Map<string, Position>>(
    () => new Map(),
  );
  const edges = useMemo(
    () => toFlowEdges(chain, highlight, SKIP_EDGES_DRAWN),
    [chain, highlight],
  );
  const { getNodes, fitView } = useReactFlow<ChainNode>();
  const initialized = useNodesInitialized();
  // Whether the layout of this chain has used the cards' measured sizes
  const measuredLayout = useRef(false);
  // The measured size of each card met so far
  const sizes = useRef(new Map<string, Size>());
  // The chain laid out last
  const laidOut = useRef(chain);
  // The highlight in force, read by the layout without running it again
  const lit = useRef(highlight);
  lit.current = highlight;

  // Once per chain: the layout with the sizes known so far
  useEffect(() => {
    const { placed, areas } = arrange(chain, sizes.current, arrangement);
    laidOut.current = chain;
    measuredLayout.current = false;
    setPositions(placed);
    setNodes([...placeAreas(areas), ...placeCards(chain, placed, lit.current)]);
    window.requestAnimationFrame(() => {
      void fitView(FIT_VIEW_OPTIONS);
    });
  }, [chain, arrangement, setNodes, fitView]);

  // A change of highlight: the cards stay where they are
  useEffect(() => {
    setNodes((current) =>
      current.map((node) => {
        if (node.data.variant === "area" || node.data.variant === "branch") {
          return node;
        }
        const dimmed =
          highlight !== null &&
          (node.data.variant === "collapsed" || !highlight.cards.has(node.id));
        return node.data.dimmed === dimmed
          ? node
          : { ...node, data: { ...node.data, dimmed } };
      }),
    );
  }, [highlight, setNodes]);

  // Once more when the cards have been measured, if a size has changed
  useEffect(() => {
    if (!initialized || measuredLayout.current) {
      return;
    }
    const shown = laidOut.current;
    measuredLayout.current = true;
    let changed = false;
    for (const node of getNodes()) {
      const { width, height } = node.measured ?? {};
      if (!width || !height || node.data.variant === "area") {
        continue;
      }
      const known = sizes.current.get(node.id);
      if (!known || known.width !== width || known.height !== height) {
        sizes.current.set(node.id, { width, height });
        changed = true;
      }
    }
    if (changed) {
      const { placed, areas } = arrange(shown, sizes.current, arrangement);
      setPositions(placed);
      // The cards keep their measures and move; the boxes are drawn afresh
      setNodes((current) => [
        ...placeAreas(areas),
        ...current
          .filter((node) => node.data.variant !== "area")
          .map((node) => ({
            ...node,
            position: placed.get(node.id) ?? node.position,
          })),
      ]);
      window.requestAnimationFrame(() => {
        void fitView(FIT_VIEW_OPTIONS);
      });
    }
  }, [initialized, getNodes, setNodes, fitView, arrangement]);

  const heads = useMemo(
    () =>
      columnHeads(chain, positions, sizes.current, arrangement ? AREA_HEAD : 0),
    [chain, positions, arrangement],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      nodesDraggable={false}
      nodesConnectable={false}
      fitView
      fitViewOptions={FIT_VIEW_OPTIONS}
      minZoom={0.2}
      maxZoom={1.5}
      colorMode="dark"
      proOptions={{ hideAttribution: true }}
      style={{ backgroundColor: "transparent" }}
    >
      <ViewportPortal>
        {heads.map((head) => (
          <Box
            key={head.rank}
            role="heading"
            aria-level={2}
            sx={(theme) => ({
              position: "absolute",
              transform: `translate(${head.x}px, ${head.y}px)`,
              width: head.width,
              height: 24,
              boxSizing: "border-box",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              borderBottom: "1px solid",
              borderColor: head.accent
                ? theme.palette.accent.main
                : alpha(theme.palette.common.white, 0.08),
              color: head.accent ? "text.primary" : "text.secondary",
              fontFamily: theme.typography.fontFamily,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              lineHeight: "16px",
            })}
          >
            {head.label}
          </Box>
        ))}
      </ViewportPortal>
      <Controls showInteractive={false} showFitView={false} />
    </ReactFlow>
  );
};

// The toolbar's "Fit": the whole chain in view
export const FitButton: React.FC = () => {
  const { fitView } = useReactFlow();
  return (
    <Button
      variant="outlined"
      size="small"
      aria-label="Fit to view"
      onClick={() => {
        void fitView(FIT_VIEW_OPTIONS);
      }}
      sx={TOOL_BUTTON_SX}
    >
      Fit
    </Button>
  );
};

// The toolbar's "Export PNG": the drawn chain, every card at full size, as
// an image file
export const ExportPngButton: React.FC<{ rootId: string }> = ({ rootId }) => {
  const { getNodes } = useReactFlow();
  const store = useStoreApi();
  const theme = useTheme();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outlined"
      size="small"
      disabled={busy}
      onClick={async () => {
        const flow = store.getState().domNode;
        if (!flow) {
          return;
        }
        setBusy(true);
        try {
          const png = await chainPng(
            flow,
            getNodes(),
            theme.palette.background.paper,
          );
          downloadPng(chainPngFilename(rootId), png);
        } catch {
          // A browser that refuses to draw the chain saves nothing
        } finally {
          setBusy(false);
        }
      }}
      sx={TOOL_BUTTON_SX}
    >
      Export PNG
    </Button>
  );
};

export default ChainCanvas;
